<template>
  <div class="selection-box">
    <div class="selection-box--list">
      <Heading :level="3" element="h3" class="selection-box--title">
        {{ titleFirstBox }}
      </Heading>
      <div
        :class="
          filteredList.length !== 0
            ? 'selection-box--content__inside-shadow'
            : null
        "
        class="selection-box--content"
      >
        <div class="search">
          <SearchInput
            v-model="filterByName"
            :placeholder="placeholder"
            class="search-input"
          />
        </div>
        <ul class="selection-box--items">
          <li class="selection-box--line__select-all" @click="selectAllItems()">
            <span class="item">
              <InputCheckbox
                :checked="areAllOptionsChecked"
                id="checkbox-select-all"
                @change="selectAllItems()"
              />
              <label class="label" for="checkbox-select-all"
                >Selecionar todos</label
              >
            </span>
          </li>
          <li
            v-for="item in filteredList"
            :key="`item-${item.id}-${item.text}`"
            class="selection-box--line"
            @click="selectItem(item)"
          >
            <span class="item">
              <InputCheckbox />
              <label :for="`checkbox-${item.id}`" class="label">{{
                item.text
              }}</label>
            </span>
          </li>
        </ul>
      </div>
    </div>
    <div class="selection-box__selected">
      <div class="selection-box--title__selected">
        <Heading :level="4" element="h4"> {{ titleSecondBox }} </Heading
        ><span class="selection-box--counter">{{ value.length }}</span>
      </div>
      <div
        :class="
          filteredSelectedList.length !== 0
            ? 'selection-box--content__inside-shadow'
            : null
        "
        class="selection-box--content"
      >
        <div v-if="value.length > 0" class="selection-box--search">
          <SearchInput
            v-model="filterSelectedByName"
            :placeholder="selectedPlaceholder"
            class="search-input"
          />
        </div>
        <ul class="selection-box--items__selected">
          <li
            v-for="item in filteredSelectedList"
            :key="`item-${item.id}-${item.text}`"
            class="selection-box--line__selected"
          >
            {{ item.text }}
            <button @click="removeItem(item)">
              <Icon :icon="'x-icon'" class="icon" />
            </button>
          </li>
        </ul>
        <div v-if="value.length === 0" class="selection-box--if-empty">
          <slot name="empty-text"
            ><p class="empty-text">{{ empty }}</p>
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * *  Componente de caixas de seleção
 */

import Heading from '@/components/_base/elements/Heading'
import Icon from '@/components/_base/elements/Icon'
import InputCheckbox from '@/components/_base/elements/InputCheckbox'
import SearchInput from '@/components/_base/elements/SearchInput'

export default {
  name: 'SelectionBox',
  components: {
    Heading,
    Icon,
    InputCheckbox,
    SearchInput
  },
  props: {
    /** Mensagem de box vazio  */
    empty: {
      type: String,
      default: 'Selecione um ou mais itens.'
    },
    /** Lista de opções
     * Ex.: [{
     * id: 1,
     * text: 'Nome'
     * }]
     */
    list: {
      type: Array,
      default: () => []
    },
    /** Placeholder da busca do primeiro box */
    placeholder: {
      type: String,
      default: 'Buscar'
    },
    /** Placeholder da busca do segundo box */
    selectedPlaceholder: {
      type: String,
      default: 'Buscar'
    },
    /** Título do primeiro box */
    titleFirstBox: {
      type: String,
      default: 'Primeiro quadro'
    },
    /** Título do segundo box */
    titleSecondBox: {
      type: String,
      default: 'Segundo quadro'
    },
    /** Valor do componente (necessário para usar v-model) */
    value: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      /** V-model da busca do primeiro box */
      filterByName: '',
      /** v-model da busca do segundo box */
      filterSelectedByName: '',

      /** Flag que controla se a lista de opções foi padronizada */
      standarizedOptions: false
    }
  },
  computed: {
    areAllOptionsChecked() {
      if (this.availableOptions.length !== 0) {
        return this.filteredList.length === 0 && this.value.length > 0
      }
      return this.value.length === this.list.length
    },
    /** Filtra as opções que já foram selecionadas para não haver repetição */
    availableOptions() {
      return this.list.filter((item) => {
        return !this.value.some((valueItem) => valueItem.id === item.id)
      })
    },
    /** Filtra por nome a lista do primeiro box */
    filteredList() {
      return this.availableOptions.filter(
        (item) =>
          item.text.toLowerCase().indexOf(this.filterByName.toLowerCase()) > -1
      )
    },
    /** Filtra por nome a lista do segundo box */
    filteredSelectedList() {
      return this.value.filter((item) => {
        return (
          item.text
            .toLowerCase()
            .indexOf(this.filterSelectedByName.toLowerCase()) > -1
        )
      })
    }
  },
  watch: {
    value() {
      this.standarizeOptionsList()
    }
  },
  methods: {
    removeAllItems() {
      this.value.splice(0, this.value.length)

      this.$emit('input', this.value)
    },
    removeItem(item) {
      this.standarizeOptionsList()

      const index = this.value.findIndex((valueItem) => {
        return valueItem.id === item.id
      })

      if (index > -1) {
        this.value.splice(index, 1)
      }
      this.$emit('input', this.value)
    },
    /** Seleciona todos os itens disponíveis */
    selectAllItems() {
      this.standarizeOptionsList()

      this.$emit('input', [...this.filteredList.slice(0), ...this.value])
      if (this.areAllOptionsChecked) {
        /**
         *  Se ao clicar em Selecionar todos a lista de disponíveis estiver vazia,
         *  remove todos os itens selecionados
         */
        this.removeAllItems()
      }
    },
    selectItem(item) {
      this.$emit('input', [item, ...this.value])
    },
    /**
     * Verifica se o item selecionado está presente na lista de opções,
     * se não estiver, o acrescenta.
     * * Para casos em que os itens selecionados não podem ser listados como opção
     * * em outros registros
     */
    standarizeOptionsList() {
      if (this.standarizedOptions) return

      this.value.forEach((item) => {
        const isInOptionsList = this.list.some(
          (option) => option.id === item.id
        )

        if (!isInOptionsList) {
          this.list.push(item)
        }
      })

      this.standarizedOptions = true
    }
  }
}
</script>

<style lang="scss" scoped>
.selection-box {
  display: flex;
  overflow: hidden;
  border-bottom: 1px solid $gray-border;
  &--items {
    cursor: pointer;
    &__selected {
      max-height: 214px;
      overflow-y: auto;
      overflow-x: hidden;
      @include scrollbars(4px, $gray-border, $white);
    }
    max-height: 214px;
    list-style-type: none;
    padding: 0;
    overflow-y: auto;
    overflow-x: hidden;
    display: flex;
    gap: 16px;
    flex-direction: column;
    padding: 16px;

    @include scrollbars(4px, $gray-border, $white);
  }
  &--line {
    cursor: pointer;
    justify-content: space-between;
    font-size: font-size('size-medium');
    line-height: 21px;
    color: $gray-darkest;
    align-items: center;
    &__select-all {
      border-bottom: 1px solid #e8e8e8;
      padding-bottom: 13px;
      font-size: font-size('size-medium');
      line-height: 21px;
      .item {
        display: flex;
        gap: 8px;
        .label {
          cursor: pointer;
        }
      }
      .label {
        cursor: pointer;
      }
    }
    &__selected {
      display: flex;
      justify-content: space-between;
      border-bottom: 1px solid $gray-border;
      padding: 14px 20px;
      font-size: font-size('size-medium');
      line-height: 21px;
      color: $gray-darkest;
    }
    .item {
      display: flex;
      gap: 8px;
      .label {
        cursor: pointer;
      }
    }
  }

  &--content {
    width: 416px;
    height: 258px;
    left: 350px;
    top: 487px;
    border: 1px solid $gray-border;
    box-sizing: border-box;
    &__inside-shadow {
      background-image: linear-gradient(0deg, $gray-lightest, transparent 19px);
    }
  }
  .search-input {
    padding-left: 18px;
    padding-right: 18px;
    border-color: $gray-border;
    height: 43px;

    &:focus {
      border-color: $primary;
    }
  }
  &__selected {
    margin-left: 25px;
    button {
      background-color: transparent;
      border-color: transparent;
      padding: 0;
      margin: 0;
      line-height: 0;
      width: 22px;
    }
    .icon {
      color: #bbbbbb;
      width: 13px;
      height: 13px;
    }

    .empty-text {
      font-size: font-size('size-small');
      line-height: 18px;
      color: $gray-dark;
    }
  }
  &--title {
    &__selected {
      display: flex;
      margin-bottom: -2px;
      align-items: baseline;
    }
  }
  &--counter {
    width: 22px;
    height: 22px;
    border-radius: 2px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    font-size: font-size('size-small');
    margin-left: 8px;
    background: $blue-2;
    color: $white;
  }
  &--if-empty {
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
</style>
